import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { ListIcon } from '@/components/icons/ListIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFormContext } from '@/components/react-hook-form';
import { AISuggestionsCountBadge } from '@/modules/aiSuggestions/components/AISuggestionsCountBadge';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import {
  CombinedSupportedSubformFieldTypes,
  EditEntityRichSection,
  EditEntitySection,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { COLORS } from '@/styles/tokens/colors';

const WIDTH = 240;

interface EditEntitySplitScreenFormSectionSwitcherProps {
  onSubmit: (afterSubmitSection?: EditEntitySection) => void;
}

export function EditEntitySplitScreenFormSectionSwitcher({
  onSubmit,
}: EditEntitySplitScreenFormSectionSwitcherProps) {
  const {
    entityType,
    currentSection,
    sectionOptions,
    clickedSubmitButton,
    setClickedSubmitButton,
  } = useEditEntitySplitScreenModalContext();

  const {
    formState: { isSubmitting },
  } = useFormContext<CombinedSupportedSubformFieldTypes>();

  const handleSectionSwitch = useCallback(
    (section: EditEntitySection) => {
      setClickedSubmitButton('section-switcher');
      onSubmit(section);
    },
    [onSubmit, setClickedSubmitButton]
  );

  // Match on representedSections rather than section: the second-death DP
  // section is represented by the consolidated "Dispositions" option.
  const currentSectionOption = useMemo(
    () =>
      sectionOptions.find((o) =>
        o.representedSections.includes(currentSection)
      ),
    [currentSection, sectionOptions]
  );

  const sectionLabel = currentSectionOption?.label ?? 'Select section to edit';

  const isSubmittingFormFromSwitcher =
    isSubmitting && clickedSubmitButton === 'section-switcher';

  return (
    <>
      <ButtonWithPopover
        data-testid="edit-entity-section-switcher"
        contentBoxSx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        size="sm"
        variant="secondary"
        popperVariant="menuBelow"
        startIcon={ListIcon}
        disabled={isSubmitting}
        loading={isSubmittingFormFromSwitcher}
        label={
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              variant="body-xs"
              color={COLORS.TEXT.PRIMARY}
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {sectionLabel}
            </Typography>
            <AISuggestionsCountBadge
              entityType={entityType}
              section={currentSectionOption?.section ?? currentSection}
            />
          </Stack>
        }
      >
        {sectionOptions.map((o) => (
          <EditEntitySectionMenuItem
            key={o.section}
            section={o}
            onRequestSectionSwitch={handleSectionSwitch}
            disabled={isSubmitting}
          />
        ))}
      </ButtonWithPopover>
    </>
  );
}

interface EditEntitySectionMenuItemProps {
  section: EditEntityRichSection;
  onRequestSectionSwitch: (section: EditEntitySection) => void;
  disabled: boolean;
}

function EditEntitySectionMenuItem({
  section,
  onRequestSectionSwitch,
  disabled,
}: EditEntitySectionMenuItemProps) {
  const { currentSection, entityType } = useEditEntitySplitScreenModalContext();

  const isSelected = section.representedSections.includes(currentSection);

  return (
    <MenuItem
      sx={{
        backgroundColor: isSelected ? COLORS.NAVY[50] : undefined,
        minWidth: WIDTH,
      }}
      justifyContent="space-between"
      label={
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>
            {section.label}
          </Typography>
          <AISuggestionsCountBadge
            entityType={entityType}
            section={section.section}
          />
        </Stack>
      }
      iconAfter={
        isSelected && (
          <CheckIcon size={16} color={COLORS.FUNCTIONAL.SUCCESS.DEFAULT} />
        )
      }
      onClick={() => onRequestSectionSwitch(section.section)}
      disabled={disabled}
    />
  );
}
